import { useSessionStore } from '@ping/ui/src/messaging/sessionStore';

import { sendConvState } from '@features/tasks/deviceGroupTaskSync';

import type { ConversationFolderMapping,ConversationSpaceMapping, Folder, Space } from './db';
import { db } from './db';

export async function createSpace(space: Omit<Space, 'createdAt' | 'updatedAt'>): Promise<Space> {
  const now = Date.now();
  const newSpace: Space = {
    ...space,
    createdAt: now,
    updatedAt: now,
  };
  await db.spaces.put(newSpace);
  return newSpace;
}

export async function updateSpace(
  id: string,
  updates: Partial<Omit<Space, 'id' | 'createdAt'>>,
): Promise<void> {
  await db.spaces.update(id, {
    ...updates,
    updatedAt: Date.now(),
  });
}

export async function deleteSpace(id: string): Promise<void> {
  await db.spaces.delete(id);
  await db.conversation_space_mappings.where('spaceId').equals(id).delete();
}

export async function getSpace(id: string): Promise<Space | undefined> {
  return db.spaces.get(id);
}

export async function listSpaces(): Promise<Space[]> {
  return db.spaces.orderBy('order').toArray();
}

export async function addConversationToSpace(
  conversationId: string,
  spaceId: string,
): Promise<void> {
  const mapping: ConversationSpaceMapping = {
    conversationId,
    spaceId,
    addedAt: Date.now(),
  };
  await db.conversation_space_mappings.put(mapping);
}

export async function removeConversationFromSpace(
  conversationId: string,
  spaceId: string,
): Promise<void> {
  await db.conversation_space_mappings
    .where(['conversationId', 'spaceId'])
    .equals([conversationId, spaceId])
    .delete();
}

export async function getConversationSpaces(conversationId: string): Promise<Space[]> {
  const mappings = await db.conversation_space_mappings
    .where('conversationId')
    .equals(conversationId)
    .toArray();

  const spaceIds = mappings.map((m) => m.spaceId);
  const spaces = await db.spaces.bulkGet(spaceIds);

  return spaces.filter((s): s is Space => s !== undefined);
}

export async function getSpaceConversations(spaceId: string): Promise<string[]> {
  const mappings = await db.conversation_space_mappings.where('spaceId').equals(spaceId).toArray();
  return mappings.map((m) => m.conversationId);
}

export async function createFolder(
  folder: Omit<Folder, 'createdAt' | 'updatedAt'>,
): Promise<Folder> {
  const now = Date.now();
  const newFolder: Folder = {
    ...folder,
    createdAt: now,
    updatedAt: now,
  };
  await db.folders.put(newFolder);
  return newFolder;
}

export async function updateFolder(
  id: string,
  updates: Partial<Omit<Folder, 'id' | 'createdAt'>>,
): Promise<void> {
  await db.folders.update(id, {
    ...updates,
    updatedAt: Date.now(),
  });
}

export async function deleteFolder(id: string): Promise<void> {
  await db.folders.delete(id);
  await db.conversation_folder_mappings.where('folderId').equals(id).delete();
}

export async function getFolder(id: string): Promise<Folder | undefined> {
  return db.folders.get(id);
}

export async function listFolders(): Promise<Folder[]> {
  return db.folders.orderBy('order').toArray();
}

export async function addConversationToFolder(
  conversationId: string,
  folderId: string,
): Promise<void> {
  const mapping: ConversationFolderMapping = {
    conversationId,
    folderId,
    addedAt: Date.now(),
  };
  await db.conversation_folder_mappings.put(mapping);
  // Mirror to the user's other devices (multi-device folder sync).
  void sendConvState(
    (useSessionStore.getState().status as { client?: unknown }).client ?? null,
    conversationId,
    { folderId },
  );
}

export async function removeConversationFromFolder(
  conversationId: string,
  folderId: string,
): Promise<void> {
  await db.conversation_folder_mappings
    .where(['conversationId', 'folderId'])
    .equals([conversationId, folderId])
    .delete();
  void sendConvState(
    (useSessionStore.getState().status as { client?: unknown }).client ?? null,
    conversationId,
    { folderRemoved: folderId },
  );
}

export async function getConversationFolders(conversationId: string): Promise<Folder[]> {
  const mappings = await db.conversation_folder_mappings
    .where('conversationId')
    .equals(conversationId)
    .toArray();

  const folderIds = mappings.map((m) => m.folderId);
  const folders = await db.folders.bulkGet(folderIds);

  return folders.filter((f): f is Folder => f !== undefined);
}

export async function getFolderConversations(folderId: string): Promise<string[]> {
  const mappings = await db.conversation_folder_mappings
    .where('folderId')
    .equals(folderId)
    .toArray();
  return mappings.map((m) => m.conversationId);
}
